import { AdditionalVisualPriceDTO, UrlDTO, VisualListResponse, VisualListResponseClient } from 'models/visuals'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { Auth0Roles } from 'models/auth'
import { AxiosResponse } from 'axios'
import { ESProduct } from 'constants/product'
import { FeeDTO } from 'models/fee'
import { IdentifiableProduct } from 'utils/serialization/productIdentifier'
import { RoleMimetype } from 'constants/API'
import { StatusResponse } from 'models/redux'
import { VisualFileType } from 'constants/visual'
import { _invalidateAssignment } from './assignment.query'
import { buildVisualsSelectionBody } from 'utils/serialization/upsellPayloads'
import { invalidateListVisuals } from './visual.query'
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAPI } from 'utils/API'

enum Endpoints {
  VISUAL_FAVORITE = '/workspace/{workspaceId}/assignment/{assignmentId}/visual/favorite',
  ADMIN_SEND_ASSIGNMENT_VISUALS_TO_CLIENT = '/assignment/{assignmentId}/deliver',
  ADMIN_SEND_ASSIGNMENT_VISUALS_TO_EDITOR = '/assignment/{assignmentId}/visual/edit',
  ADMIN_REMOVE_DELIVERED_VISUALS = '/assignment/{assignmentId}/visual/delivered/removal',
  VISUAL_ADDITIONAL_PRICE_WITH_WORKSPACE = '/workspace/{workspaceId}/assignment/{assignmentId}/additionalVisualPrice',
  VISUAL_ADDITIONAL_PRICE = '/assignment/{assignmentId}/additionalVisualPrice',
  VISUAL_GET_VIRTUAL_VISIT_EXTENSION_PRICE_WITH_WORKSPACE = '/workspace/{workspaceId}/assignment/{assignmentId}/virtualVisit/extendExpiration/price',
  VISUAL_GET_VIRTUAL_VISIT_EXTENSION_PRICE = '/assignment/{assignmentId}/virtualVisit/extendExpiration/price',
  LIST_VISUALS = '/assignment/{assignmentId}/visual/all',
  LIST_VISUALS_WITH_WORKSPACE = '/workspace/{workspaceId}/assignment/{assignmentId}/visual/all',
  GET_VISUAL_BY_NAME = '/workspace/{workspaceId}/assignment/{assignmentId}/visual/{filename}',
  SELECT_VISUALS_WITH_WORKSPACE = '/workspace/{workspaceId}/assignment/{assignmentId}/visual/select',
  SELECT_VISUALS = '/assignment/{assignmentId}/visual/select',
  RENAME_VISUAL_WITH_WORKSPACE = '/workspace/{workspaceId}/assignment/{assignmentId}/visual/{filename}/metadata',
  RENAME_VISUAL = '/assignment/{assignmentId}/visual/{filename}/metadata',
}

interface VirtualVisitExtensionPriceDTO {
  fee: FeeDTO
  productId: number
}

// QUERIES

export const invalidateAssignmentVisuals = (client: QueryClient, assignmentId: string, visualType: VisualFileType) => {
  client.invalidateQueries({ queryKey: [EntityKeys.ASSIGNMENT_VISUAL, QueryType.LIST, { assignmentId, visualType }] })
}

/** Only used in creative */
export function useGetAssignmentVisuals(assignmentId: string, visualType: VisualFileType) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT_VISUAL, QueryType.LIST, { assignmentId, visualType }],
    queryFn: () => {
      return api.get<VisualListResponse>(
        Endpoints.LIST_VISUALS,
        { assignmentId },
        true,
        {
          params: {
            type: visualType,
          },
        }
      )
    },
    enabled: !!assignmentId && !!visualType,
  })
}

/** Lists all visuals for an assignment with workspace context. */
export function useGetAssignmentVisualsWithWorkspace(assignmentId: string, workspaceId: string, visualType: VisualFileType) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT_VISUAL, QueryType.LIST, { assignmentId, visualType, workspaceId }],
    queryFn: () => api.get<VisualListResponseClient>(
      Endpoints.LIST_VISUALS_WITH_WORKSPACE,
      { assignmentId, workspaceId },
      false,
      {
        params: {
          type: visualType,
        },
      }
    ),
    enabled: !!assignmentId && !!workspaceId && !!visualType,
  })
}

export function useGetAdditionalVisualPrice(assignmentId: string, workspaceId: string, role: Auth0Roles, canFetch = true) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT_VISUAL_PRICE, QueryType.GET_ONE, { assignmentId }],
    queryFn: () => api.get<AdditionalVisualPriceDTO>(
      role.isAdmin ? Endpoints.VISUAL_ADDITIONAL_PRICE : Endpoints.VISUAL_ADDITIONAL_PRICE_WITH_WORKSPACE,
      { assignmentId, workspaceId },
      true,
      {
        headers: {
          Accept: RoleMimetype.CLIENT // TODO: Fix on BE so we can send proper accept header
        }
      }
    ),
    enabled: !!assignmentId && (role.isClient || role.isAdmin) && canFetch,
  })
}

export function useGetVirtualVisitExtensionPrice(assignmentId: string, workspaceId: string, role: Auth0Roles) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.VIRTUAL_VISIT_EXTENSION_PRICE, QueryType.GET_ONE, { assignmentId }],
    queryFn: () => api.get<VirtualVisitExtensionPriceDTO>(
      role.isClient ? Endpoints.VISUAL_GET_VIRTUAL_VISIT_EXTENSION_PRICE_WITH_WORKSPACE : Endpoints.VISUAL_GET_VIRTUAL_VISIT_EXTENSION_PRICE,
      { assignmentId, workspaceId },
      true,
      {
        headers: {
          Accept: RoleMimetype.CLIENT // TODO: Fix on BE so we can send proper accept header
        }
      }
    ),
  })
}

// MUTATIONS

interface SelectVisualsPayload {
  assignmentId: string
  filenames: string[]
  /** The assignment's own product — passed whole so the request can identify it by its Product2 code. */
  product: IdentifiableProduct
  workspaceId: string
  /** Only clients own a workspace; anyone else would otherwise request /workspace//assignment/... */
  isClient: boolean
  /** The payment option the client picked. Absent when the selection needs no payment. */
  billingProduct?: IdentifiableProduct
  paymentIntentId?: string
}

export function useSelectVisuals() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<StatusResponse>, SelectVisualsPayload>(
    ({ assignmentId, workspaceId, isClient, filenames, product, billingProduct, paymentIntentId }) => api.post(
      isClient ? Endpoints.SELECT_VISUALS_WITH_WORKSPACE : Endpoints.SELECT_VISUALS,
      { assignmentId, workspaceId },
      buildVisualsSelectionBody({
        filenames,
        product,
        billingProduct,
        paymentIntentId,
        correlation: { assignmentId, workspaceId, filenameCount: filenames.length },
      }),
      true,
      { headers: { Accept: RoleMimetype.CLIENT } }
    ),
    (client, { assignmentId, paymentIntentId }) => {
      if (!paymentIntentId) {
        invalidateListVisuals(client, assignmentId)
      }
    }
  )
}

export interface AdminSendVisualsToEditorPayload {
  assignmentId: string,
  filenames: string[],
  product: ESProduct,
  comment: string,
  /**
   * Reference of the open Esoft order the admin confirmed replacing. The backend answers 409 unless it is
   * the line's newest open order, so a send never replaces an order the admin did not see.
   */
  replaceOpenOrderReference?: string,
}

/** Mutation for adding favorite visual */
export function useAddFavoriteVisual() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, { assignmentId: string, workspaceId: string, visuals: string[], }>(
    ({ assignmentId, workspaceId, visuals }) => api.post(
      Endpoints.VISUAL_FAVORITE,
      {
        assignmentId: assignmentId.toString(),
        workspaceId,
      },
      {
        visuals
      },
      false
    )
  )
}

/** Mutation for removing favorite visual */
export function useDeleteFavoriteVisual() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, { assignmentId: string, workspaceId: string, visuals: string[] }>(
    ({ assignmentId, workspaceId, visuals }) => api.delete(
      Endpoints.VISUAL_FAVORITE,
      {
        assignmentId: assignmentId.toString(),
        workspaceId,
      },
      false,
      {
        data: {
          visuals
        }
      }
    )
  )
}

/** Mutation for send assignment visuals to client */
export function useSendVisualsToClient() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<StatusResponse>, { assignmentId: string }>(
    ({ assignmentId }) => api.post(
      Endpoints.ADMIN_SEND_ASSIGNMENT_VISUALS_TO_CLIENT,
      {
        assignmentId,
      },
      {},
      false
    )
  )
}

/** Mutation for send assignment visuals to editor */
export function useSendVisualsToEditor() {
  const api = useAPI<Endpoints>()
  const queryClient = useQueryClient()

  return getMutation<AxiosResponse<StatusResponse>, AdminSendVisualsToEditorPayload>(
    ({ assignmentId, filenames, product, comment, replaceOpenOrderReference }) => api.post(
      Endpoints.ADMIN_SEND_ASSIGNMENT_VISUALS_TO_EDITOR,
      {
        assignmentId,
      },
      {
        filenames,
        product,
        comment,
        visualType: VisualFileType.RAW,
        ...(replaceOpenOrderReference ? { replaceOpenOrderReference } : {}),
      },
      false,
      undefined,
      // An expected refusal, e.g. Esoft opened another order meanwhile; the gallery shows it.
      { ignoreErrorFactory: error => error.response?.status === 409 }
    ),
    (client, { assignmentId }) => {
      // The round moves the assignment back into the editing lane; a stale stage reads as "nothing happened".
      _invalidateAssignment(client, assignmentId)
    },
    {
      // A 409 can mean the open Esoft order changed meanwhile; the refetch shows the current one.
      onError: (_error, { assignmentId }) => _invalidateAssignment(queryClient, assignmentId),
    }
  )
}

export interface RemoveDeliveredVisualsPayload {
  assignmentId: string
  filenames: string[]
  reason: string
}

export interface RemoveDeliveredVisualsResult {
  removed: string[]
  failed: string[]
}

/** Mutation for an admin removing visuals already delivered to the client, with the reason for the audit log */
export function useRemoveDeliveredVisuals() {
  const api = useAPI<Endpoints>()
  const queryClient = useQueryClient()

  return getMutation<AxiosResponse<RemoveDeliveredVisualsResult>, RemoveDeliveredVisualsPayload>(
    ({ assignmentId, filenames, reason }) => api.post(
      Endpoints.ADMIN_REMOVE_DELIVERED_VISUALS,
      {
        assignmentId,
      },
      {
        filenames,
        reason,
      },
      true
    ),
    (client, { assignmentId }) => {
      invalidateListVisuals(client, assignmentId)
      _invalidateAssignment(client, assignmentId)
    },
    {
      // A 404/409 usually means the gallery is stale; the refetch drops the gone names from the selection.
      onError: (_error, { assignmentId }) => {
        invalidateListVisuals(queryClient, assignmentId)
        _invalidateAssignment(queryClient, assignmentId)
      },
    }
  )
}

export interface VisualByTypeNamePayload {
  assignmentId: string,
  workspaceId: string,
  visualType: VisualFileType,
  visualName: string
}

/** Mutation for getting one specific visual by type and name. Only used in client. */
export function useGetAssignmentVisualByTypeName() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<UrlDTO>, VisualByTypeNamePayload>(
    ({ assignmentId, workspaceId, visualType, visualName }) => api.post(
      Endpoints.GET_VISUAL_BY_NAME,
      {
        assignmentId,
        workspaceId,
        filename: visualName,
      },
      {},
      true,
      { params: { type: visualType } }
    )
  )
}

export interface RenameVisualPayload {
  assignmentId: string,
  workspaceId: string,
  originalFilename: string,
  filename: string,
  isClient: boolean,
}

export function useRenameVisual() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<StatusResponse>, RenameVisualPayload>(
    ({ originalFilename, filename, assignmentId, workspaceId, isClient }) => api.put(
      isClient ? Endpoints.RENAME_VISUAL_WITH_WORKSPACE : Endpoints.RENAME_VISUAL,
      {
        assignmentId: assignmentId.toString(),
        workspaceId,
        filename
      },
      { originalFilename },
      true
    ),
    (client, { assignmentId }) => {
      _invalidateAssignment(client, assignmentId)
    }
  )
}
